// Shared loading / error state views used across pages.

import { Banner, Card, Spinner } from "@shopify/polaris";
import type { ReactNode } from "react";

export function LoadingCard({ label = "Loading" }: { label?: string }) {
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "center", padding: "2.5rem" }}>
        <Spinner accessibilityLabel={label} size="large" />
      </div>
    </Card>
  );
}

export function ErrorState({
  message,
  onRetry,
  title = "Something went wrong",
}: {
  message: string;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <Banner
      tone="critical"
      title={title}
      action={onRetry ? { content: "Retry", onAction: onRetry } : undefined}
    >
      <p>{message}</p>
    </Banner>
  );
}

// Renders loading / error / empty / content in one place so pages stay flat.
export function AsyncSection<T>({
  loading,
  error,
  data,
  onRetry,
  isEmpty,
  empty,
  loadingLabel,
  children,
}: {
  loading: boolean;
  error: string | null;
  data: T | null;
  onRetry?: () => void;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loadingLabel?: string;
  children: (data: T) => ReactNode;
}) {
  if (loading && !data) return <LoadingCard label={loadingLabel} />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!data) return <ErrorState message="No data returned." onRetry={onRetry} />;
  if (isEmpty && isEmpty(data) && empty) return <>{empty}</>;
  return <>{children(data)}</>;
}
